import { useQuery } from 'react-query'
import client, { publicClient } from './api-client'

const queryConfig = {
  staleTime: 1000 * 60 * 60 * 12, // 12 hours (before GEE URL expires)
  cacheTime: 1000 * 60 * 60 * 12,
}

async function fetchHansenTileUrl() {
  const data = await client('earth-engine/hansen-tile-url/')
  return data
}

async function fetchHansenTileUrlWithAuth(isPublic = false) {
  if (isPublic) {
    // Use public client with x-api-key authentication
    return await publicClient('earth-engine/hansen-tile-url/')
  } else {
    // Use existing JWT-based client
    return await fetchHansenTileUrl()
  }
}

export function useHansenTileUrl(isPublic = false, enabled = false) {
  return useQuery(
    ['hansen-tile-url', isPublic],
    () => fetchHansenTileUrlWithAuth(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchJrcGfc2020TileUrlWithAuth(isPublic = false, version = 3) {
  const endpoint = `earth-engine/jrc-gfc2020-tile-url/${version === 3 ? '' : `?version=${encodeURIComponent(version)}`}`
  return isPublic ? publicClient(endpoint) : client(endpoint)
}

export function useJrcGfc2020TileUrl(isPublic = false, enabled = false, version = 3) {
  return useQuery(
    ['jrc-gfc2020-tile-url', isPublic, version],
    () => fetchJrcGfc2020TileUrlWithAuth(isPublic, version),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchForTy2020TileUrl() {
  const data = await client('earth-engine/forty-2020-tile-url/')
  return data
}

async function fetchForTy2020TileUrlWithAuth(isPublic = false) {
  if (isPublic) {
    return await publicClient('earth-engine/forty-2020-tile-url/')
  } else {
    return await fetchForTy2020TileUrl()
  }
}

export function useForTy2020TileUrl(isPublic = false, enabled = false) {
  return useQuery(
    ['forty-2020-tile-url', isPublic],
    () => fetchForTy2020TileUrlWithAuth(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchNaturalForest2020TileUrlWithAuth(isPublic = false) {
  if (isPublic) {
    return await publicClient('earth-engine/natural-forest-2020-tile-url/')
  }
  return await client('earth-engine/natural-forest-2020-tile-url/')
}

export function useNaturalForest2020TileUrl(isPublic = false, enabled = false) {
  return useQuery(
    ['natural-forest-2020-tile-url', isPublic],
    () => fetchNaturalForest2020TileUrlWithAuth(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchDistAlertTileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/dist-alert-tile-url/') : await client('earth-engine/dist-alert-tile-url/')
  return data
}

export function useDistAlertTileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['dist-alert-tile-url', isPublic],
    () => fetchDistAlertTileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchGladLAfter2020TileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/glad-l-after-2020-tile-url/') : await client('earth-engine/glad-l-after-2020-tile-url/')
  return data
}

export function useGladLAfter2020TileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['glad-l-after-2020-tile-url', isPublic],
    () => fetchGladLAfter2020TileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchGladLBefore2020TileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/glad-l-before-2020-tile-url/') : await client('earth-engine/glad-l-before-2020-tile-url/')
  return data
}

export function useGladLBefore2020TileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['glad-l-before-2020-tile-url', isPublic],
    () => fetchGladLBefore2020TileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchGladS2After2020TileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/glad-s2-after-2020-tile-url/') : await client('earth-engine/glad-s2-after-2020-tile-url/')
  return data
}

export function useGladS2After2020TileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['glad-s2-after-2020-tile-url', isPublic],
    () => fetchGladS2After2020TileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchGladS2Before2020TileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/glad-s2-before-2020-tile-url/') : await client('earth-engine/glad-s2-before-2020-tile-url/')
  return data
}

export function useGladS2Before2020TileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['glad-s2-before-2020-tile-url', isPublic],
    () => fetchGladS2Before2020TileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchGfwIntegratedAlertsTileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/gfw-integrated-alerts-tile-url/') : await client('earth-engine/gfw-integrated-alerts-tile-url/')
  return data
}

export function useGfwIntegratedAlertsTileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['gfw-integrated-alerts-tile-url', isPublic],
    () => fetchGfwIntegratedAlertsTileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchRaddAfter2020TileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/radd-after-2020-tile-url/') : await client('earth-engine/radd-after-2020-tile-url/')
  return data
}

export function useRaddAfter2020TileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['radd-after-2020-tile-url', isPublic],
    () => fetchRaddAfter2020TileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchRaddBefore2020TileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/radd-before-2020-tile-url/') : await client('earth-engine/radd-before-2020-tile-url/')
  return data
}

export function useRaddBefore2020TileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['radd-before-2020-tile-url', isPublic],
    () => fetchRaddBefore2020TileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchAqueductWaterStressTileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/aqueduct-water-stress-tile-url/') : await client('earth-engine/aqueduct-water-stress-tile-url/')
  return data
}

export function useAqueductWaterStressTileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['aqueduct-water-stress-tile-url', isPublic],
    () => fetchAqueductWaterStressTileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

async function fetchAqueductEsgRiskTileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/aqueduct-esg-risk-tile-url/') : await client('earth-engine/aqueduct-esg-risk-tile-url/')
  return data
}

export function useAqueductEsgRiskTileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['aqueduct-esg-risk-tile-url', isPublic],
    () => fetchAqueductEsgRiskTileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: (data) => ({ tile_url: data.tile_url, legend: data.legend, description: data.description }),
    }
  )
}

// Layers served by the Global Nature Watch tile cache or Earth Engine that
// carry extra render hints from the backend: `attribution` (mandatory credit
// for CC BY / NASA data), `blend_mode` (CSS mix-blend-mode for opaque static
// caches) and `max_native_zoom` (static caches published up to a fixed zoom).
const selectTileLayerWithHints = (data) => ({
  tile_url: data.tile_url,
  legend: data.legend,
  description: data.description,
  attribution: data.attribution,
  blend_mode: data.blend_mode,
  max_native_zoom: data.max_native_zoom,
  // Windowed layers (VIIRS fires) echo the rendered window.
  start_date: data.start_date,
  end_date: data.end_date,
  days: data.days,
})

async function fetchAlertDriversTileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/alert-drivers-tile-url/') : await client('earth-engine/alert-drivers-tile-url/')
  return data
}

export function useAlertDriversTileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['alert-drivers-tile-url', isPublic],
    () => fetchAlertDriversTileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: selectTileLayerWithHints,
    }
  )
}

async function fetchTreeCoverLossFiresTileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/tree-cover-loss-fires-tile-url/') : await client('earth-engine/tree-cover-loss-fires-tile-url/')
  return data
}

export function useTreeCoverLossFiresTileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['tree-cover-loss-fires-tile-url', isPublic],
    () => fetchTreeCoverLossFiresTileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: selectTileLayerWithHints,
    }
  )
}

// Window of the VIIRS layer, in days including today (7 = today and the six
// previous days). The backend default is 7; the drawer offers 7 / 30 / 90.
export const VIIRS_FIRE_ALERT_DEFAULT_DAYS = 7

async function fetchViirsFireAlertsTileUrl(isPublic = false, days = VIIRS_FIRE_ALERT_DEFAULT_DAYS) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const endpoint = `earth-engine/viirs-fire-alerts-tile-url/?days=${encodeURIComponent(days)}`
  const data = isPublic ? await publicClient(endpoint) : await client(endpoint)
  return data
}

// The rolling window is anchored to "now" when the tile URL is generated,
// so a URL kept for 12 h would show a window that ended 12 h ago. One hour
// keeps the window honest at the cost of one getMapId per hour.
const VIIRS_QUERY_CONFIG = {
  staleTime: 1000 * 60 * 60,
  cacheTime: 1000 * 60 * 60,
}

export function useViirsFireAlertsTileUrl(enabled = false, isPublic = false, days = VIIRS_FIRE_ALERT_DEFAULT_DAYS) {
  return useQuery(
    ['viirs-fire-alerts-tile-url', isPublic, days],
    () => fetchViirsFireAlertsTileUrl(isPublic, days),
    {
      enabled,
      ...VIIRS_QUERY_CONFIG,
      select: selectTileLayerWithHints,
    }
  )
}

async function fetchTreeCoverLossDriversTileUrl(isPublic = false) {
  // Public one-shot map has no JWT: it authenticates with the pb_ API key.
  const data = isPublic ? await publicClient('earth-engine/tree-cover-loss-drivers-tile-url/') : await client('earth-engine/tree-cover-loss-drivers-tile-url/')
  return data
}

export function useTreeCoverLossDriversTileUrl(enabled = false, isPublic = false) {
  return useQuery(
    ['tree-cover-loss-drivers-tile-url', isPublic],
    () => fetchTreeCoverLossDriversTileUrl(isPublic),
    {
      enabled,
      ...queryConfig,
      select: selectTileLayerWithHints,
    }
  )
}

export function useGeeAlertLayers(isCompany400, layerVisibility, isPublic = false, windowDays = {}) {
  const distAlert = useDistAlertTileUrl(isCompany400 && layerVisibility['DIST-ALERT'], isPublic)
  const gladLAfter = useGladLAfter2020TileUrl(isCompany400 && layerVisibility['GLAD-L after 2020'], isPublic)
  const gladLBefore = useGladLBefore2020TileUrl(isCompany400 && layerVisibility['GLAD-L before 2020'], isPublic)
  const gladS2After = useGladS2After2020TileUrl(isCompany400 && layerVisibility['GLAD-S2 after 2020'], isPublic)
  const gladS2Before = useGladS2Before2020TileUrl(isCompany400 && layerVisibility['GLAD-S2 before 2020'], isPublic)
  const gfwIntegrated = useGfwIntegratedAlertsTileUrl(isCompany400 && layerVisibility['Integrated Alerts (GFW)'], isPublic)
  const raddAfter = useRaddAfter2020TileUrl(isCompany400 && layerVisibility['RADD after 2020'], isPublic)
  const raddBefore = useRaddBefore2020TileUrl(isCompany400 && layerVisibility['RADD before 2020'], isPublic)
  const waterStress = useAqueductWaterStressTileUrl(isCompany400 && layerVisibility['Water Stress (Aqueduct)'], isPublic)
  const esgRisk = useAqueductEsgRiskTileUrl(isCompany400 && layerVisibility['ESG Risk (Aqueduct)'], isPublic)
  const alertDrivers = useAlertDriversTileUrl(isCompany400 && layerVisibility['Drivers of Deforestation Alerts'], isPublic)
  const viirsFires = useViirsFireAlertsTileUrl(isCompany400 && layerVisibility['Fire Detections (VIIRS)'], isPublic, windowDays['Fire Detections (VIIRS)'] || VIIRS_FIRE_ALERT_DEFAULT_DAYS)
  const treeCoverLossFires = useTreeCoverLossFiresTileUrl(isCompany400 && layerVisibility['Tree Cover Loss from Fires'], isPublic)
  const treeCoverLossDrivers = useTreeCoverLossDriversTileUrl(isCompany400 && layerVisibility['Tree Cover Loss Drivers (1 km)'], isPublic)

  return {
    'DIST-ALERT': distAlert,
    'GLAD-L after 2020': gladLAfter,
    'GLAD-L before 2020': gladLBefore,
    'GLAD-S2 after 2020': gladS2After,
    'GLAD-S2 before 2020': gladS2Before,
    'Integrated Alerts (GFW)': gfwIntegrated,
    'RADD after 2020': raddAfter,
    'RADD before 2020': raddBefore,
    'Water Stress (Aqueduct)': waterStress,
    'ESG Risk (Aqueduct)': esgRisk,
    'Drivers of Deforestation Alerts': alertDrivers,
    'Fire Detections (VIIRS)': viirsFires,
    'Tree Cover Loss from Fires': treeCoverLossFires,
    'Tree Cover Loss Drivers (1 km)': treeCoverLossDrivers,
  }
}
